import React, { useEffect, useId } from "react";

import { useIMask } from "react-imask";

import { Field, FieldError, FieldLabel } from "@/src/components/ui/field";
import { Input } from "@/src/components/ui/input";

import type { JSX } from "react";

type MaskOptions = Parameters<typeof useIMask>[0];

interface MaskedTextFieldBaseProps {
  maskOptions: MaskOptions;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
}

/**
 * Controlled text field whose input is driven by IMask (via `useIMask`), so the
 * separators are inserted and invalid characters rejected as the user types.
 * Mirrors `TextFieldBase`'s controlled API (value / onChange / onBlur / error).
 */
function MaskedTextFieldBase({
  maskOptions,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  label,
  placeholder,
}: MaskedTextFieldBaseProps): JSX.Element {
  const id = useId();
  const {
    ref,
    value: maskValue,
    setValue,
  } = useIMask<HTMLInputElement>(maskOptions, {
    onAccept: (accepted) => onChange(accepted),
  });

  // Sync the mask when the value changes from outside (e.g. switching media).
  // The guard skips re-applying the value the mask itself just produced.
  useEffect(() => {
    if (value !== maskValue) setValue(value);
  }, [value, maskValue, setValue]);

  return (
    <Field>
      {label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
      <Input
        ref={ref}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        id={id}
        onBlur={onBlur}
        placeholder={placeholder}
      />
      <FieldError>{error}</FieldError>
    </Field>
  );
}

export default MaskedTextFieldBase;
